import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import ConfirmationModal from '../core/ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ImageCropper from '../core/ImageCropper/ImageCropper';
import LandingPagePreviewModal from './LandingPagePreviewModal';
import FormattingToolbar from '../shared/FormattingToolbar';
import { applyTextFormatting } from '../../utils/formatText';
import { Image as ImageIcon, Plus, X, Eye, Info } from 'lucide-react';

// Import reliable fetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';

// Auth token helper - uses supabaseFetch.getAuthToken() which auto-refreshes expired JWTs
const getAuthToken = () => supabaseFetch.getAuthToken();

/**
 * AddEditLandingPageGroup - Edit landing page for a group
 * Based on G2_GROUPS_INFO-FRONT_PAGE_D.pdf design
 * Two-column layout with inline images support
 */
const AddEditLandingPageGroup = ({ userId, onBack, group, postCreate = false }) => {
  const { t } = useTranslation('groups');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [landingPageId, setLandingPageId] = useState(null);

  // Setup-end handoff banner (functional-fallback build brief §4) — the handoff itself is the
  // notice; dismissable like AdminGroup's own post-create banner, same idiom.
  const [showPostCreateBanner, setShowPostCreateBanner] = useState(postCreate);

  // Logo click listener - navigate back to MainFeed
  // Asks first when there are unsaved changes (#78); confirmLeaveRef is set once the hook below runs.
  const confirmLeaveRef = useRef(null);
  useEffect(() => {
    const handleLogoClick = async () => {
      const leave = confirmLeaveRef.current ? await confirmLeaveRef.current() : true;
      if (leave && onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Content state
  const [leftColumnContent, setLeftColumnContent] = useState('');
  const [rightColumnContent, setRightColumnContent] = useState('');

  // Inline images state
  const [leftInlineImages, setLeftInlineImages] = useState([]);
  const [rightInlineImages, setRightInlineImages] = useState([]);

  // Image cropper state
  const [showLeftCropper, setShowLeftCropper] = useState(false);
  const [showRightCropper, setShowRightCropper] = useState(false);

  // Textarea refs for cursor position tracking
  const leftTextareaRef = useRef(null);
  const rightTextareaRef = useRef(null);
  const [leftCursorPos, setLeftCursorPos] = useState(0);
  const [rightCursorPos, setRightCursorPos] = useState(0);

  // Modal state
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);

  // Publication state
  const [isPublished, setIsPublished] = useState(false);
  // #78: true once the saved page (or its first draft) is in the form.
  const [pageLoaded, setPageLoaded] = useState(!group?.id);

  // #78 items 4–5: "unsaved changes" means changed since the page loaded or was last saved.
  const { confirmLeave, rebase, dialog: leaveDialog } = useUnsavedChanges({
    value: { leftColumnContent, rightColumnContent, leftInlineImages, rightInlineImages },
    ready: pageLoaded,
    onLeave: () => { if (onBack) onBack(); },
  });
  confirmLeaveRef.current = confirmLeave;

  // Page title
  const pageTitle = `${group?.name || t('landingPage.group')} ${t('landingPage.title')}`;

  // Load existing landing page data
  useEffect(() => {
    if (!group?.id) return;

    const loadLandingPage = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabaseFetch.query('community_landing', {
          select: '*',
          eq: { community_id: group.id },
          single: true
        });

        if (error) {
          console.error('Error loading landing page:', error);
        }

        if (data) {
          setLandingPageId(data.id);
          setLeftColumnContent(data.left_column_content || '');
          setRightColumnContent(data.right_column_content || '');
          setLeftInlineImages(data.left_inline_images || []);
          setRightInlineImages(data.right_inline_images || []);
          setIsPublished(data.is_published || false);
        } else {
          // No landing page yet — seed a real draft from the group's own description (per the
          // functional-fallback build brief §4) instead of opening blank. Only applies when no
          // community_landing row exists; an existing row's content is never overwritten.
          const { data: community, error: descError } = await supabaseFetch.query('communities', {
            select: 'description',
            eq: { id: group.id },
            single: true
          });
          if (descError) {
            console.error('Error loading group description for prefill:', descError);
          }
          if (community?.description) {
            setLeftColumnContent(community.description);
          }
        }
      } catch (err) {
        console.error('Error loading landing page:', err);
      } finally {
        setLoading(false);
        setPageLoaded(true);
      }
    };

    loadLandingPage();
  }, [group?.id]);

  // Image size options: S = 33%, M = 66%, L = 100%
  const IMAGE_SIZES = {
    S: '33%',
    M: '66%',
    L: '100%'
  };

  // Helper to build image marker with proper paragraph spacing
  const buildImageMarker = (imageId, content, cursorPos) => {
    const before = content.substring(0, cursorPos);
    const after = content.substring(cursorPos);

    // Check if we need paragraph breaks before/after
    const needsBreakBefore = before.length > 0 && !before.endsWith('\n\n');
    const needsBreakAfter = after.length > 0 && !after.startsWith('\n\n');

    // Build the marker with appropriate spacing
    const prefix = needsBreakBefore ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const suffix = needsBreakAfter ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
    const marker = `${prefix}[IMG:${imageId}]${suffix}`;

    return {
      newContent: before + marker + after,
      markerLength: marker.length
    };
  };

  // The formatting toolbar (group form brief 2026-10-04, addendum 2 item 2): the same toolbar and
  // text edit as the article editor. Text stays plain, with markers; the Introduction page renders
  // it through formatTextWithStyles, as articles are rendered.
  const formatColumn = (column, type) => {
    const isLeft = column === 'left';
    const textarea = (isLeft ? leftTextareaRef : rightTextareaRef).current;
    if (!textarea) return;
    const result = applyTextFormatting(
      isLeft ? leftColumnContent : rightColumnContent,
      textarea.selectionStart, textarea.selectionEnd, type,
      { bold: t('articles:bold_text'), italic: t('articles:italic_text') }
    );
    if (!result) return;
    (isLeft ? setLeftColumnContent : setRightColumnContent)(result.text);
    (isLeft ? setLeftCursorPos : setRightCursorPos)(result.cursor);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(result.cursor, result.cursor);
    }, 0);
  };

  // Handle left column inline image save - inserts at cursor position
  const handleLeftImageSave = ({ displayUrl, thumbnailUrl }) => {
    const newImage = {
      id: `img-left-${Date.now()}`,
      displayUrl,
      thumbnailUrl,
      position: leftCursorPos,
      size: 'L' // Default to large (100%)
    };
    setLeftInlineImages([...leftInlineImages, newImage]);

    // Insert image marker at cursor position with proper paragraph spacing
    const { newContent, markerLength } = buildImageMarker(newImage.id, leftColumnContent, leftCursorPos);
    setLeftColumnContent(newContent);

    // Update cursor position to after the inserted marker
    setLeftCursorPos(leftCursorPos + markerLength);
  };

  // Handle right column inline image save - inserts at cursor position
  const handleRightImageSave = ({ displayUrl, thumbnailUrl }) => {
    const newImage = {
      id: `img-right-${Date.now()}`,
      displayUrl,
      thumbnailUrl,
      position: rightCursorPos,
      size: 'L' // Default to large (100%)
    };
    setRightInlineImages([...rightInlineImages, newImage]);

    // Insert image marker at cursor position with proper paragraph spacing
    const { newContent, markerLength } = buildImageMarker(newImage.id, rightColumnContent, rightCursorPos);
    setRightColumnContent(newContent);

    // Update cursor position to after the inserted marker
    setRightCursorPos(rightCursorPos + markerLength);
  };

  // Handle image size change
  const handleLeftImageSize = (imageId, newSize) => {
    setLeftInlineImages(leftInlineImages.map(img =>
      img.id === imageId ? { ...img, size: newSize } : img
    ));
  };

  const handleRightImageSize = (imageId, newSize) => {
    setRightInlineImages(rightInlineImages.map(img =>
      img.id === imageId ? { ...img, size: newSize } : img
    ));
  };

  // Remove inline image from left column
  const handleRemoveLeftImage = (imageId) => {
    setLeftInlineImages(leftInlineImages.filter(img => img.id !== imageId));
    setLeftColumnContent(prev => prev.replace(`[IMG:${imageId}]`, ''));
  };

  // Remove inline image from right column
  const handleRemoveRightImage = (imageId) => {
    setRightInlineImages(rightInlineImages.filter(img => img.id !== imageId));
    setRightColumnContent(prev => prev.replace(`[IMG:${imageId}]`, ''));
  };

  // Render content with inline images (with size support)
  const renderContentWithImages = (content, images) => {
    if (!content) return null;

    // Split content by image markers
    const parts = content.split(/(\[IMG:[^\]]+\])/g);

    return parts.map((part, index) => {
      // Check if this is an image marker
      const imageMatch = part.match(/\[IMG:([^\]]+)\]/);
      if (imageMatch) {
        const imageId = imageMatch[1];
        const image = images.find(img => img.id === imageId);
        if (image) {
          const imageWidth = IMAGE_SIZES[image.size] || '100%';
          return (
            <div key={index} style={{ margin: '16px 0', position: 'relative' }}>
              <img
                src={image.displayUrl}
                alt=""
                style={{
                  width: imageWidth,
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            </div>
          );
        }
        return null;
      }

      // Regular text content
      if (part.trim()) {
        return (
          <p key={index} style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#374151',
            whiteSpace: 'pre-wrap',
            marginBottom: '12px'
          }}>
            {part}
          </p>
        );
      }
      return null;
    });
  };

  // Save landing page
  const handleSave = async (publish = false) => {
    if (!group?.id) return;

    setSaving(true);
    try {
      const landingData = {
        community_id: group.id,
        left_column_content: leftColumnContent,
        right_column_content: rightColumnContent,
        left_inline_images: leftInlineImages,
        right_inline_images: rightInlineImages,
        is_published: publish,
        updated_by: userId,
        updated_at: new Date().toISOString()
      };

      if (publish) {
        landingData.published_at = new Date().toISOString();
      }

      const authToken = await getAuthToken();
      let data, error;

      if (landingPageId) {
        // Update existing
        ({ data, error } = await supabaseFetch.update('community_landing', landingData, {
          eq: { id: landingPageId },
          authToken
        }));
      } else {
        // Insert new
        landingData.created_by = userId;
        ({ data, error } = await supabaseFetch.insert('community_landing', landingData, { authToken }));

        if (data) {
          setLandingPageId(data.id);
        }
      }

      if (error) throw error;

      setIsPublished(publish);
      rebase(); // saved: what is on screen is now the saved page

      setModal({
        visible: true,
        message: publish ? t('landingPage.publishSuccess') : t('landingPage.draftSuccess'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } catch (err) {
      console.error('Error saving landing page:', err);
      setModal({
        visible: true,
        message: t('landingPage.saveError', { error: err.message || t('landingPage.unknownError') }),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel asks only when something changed (#78).
  const handleCancel = async () => {
    if ((await confirmLeave()) && onBack) onBack();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6b7280' }}>{t('landingPage.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
      {/* Header */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={{ name: pageTitle }}
        onBack={onBack}
        headerTitle={t('landingPage.headerTitle')}
        onCheckUnsaved={confirmLeave}
      />

      {/* Setup-end handoff banner — only when reached right after group creation */}
      {showPostCreateBanner && (
        <div style={{
          position: 'relative',
          margin: '100px 40px 0 40px',
          padding: '14px 44px 14px 16px',
          background: '#EBF5FF',
          border: '1px solid #BFDBFE',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Info size={20} color="#2563EB" style={{ flexShrink: 0 }} />
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#1E40AF',
            lineHeight: '1.4'
          }}>
            {t('landing_setup_prompt')}
          </p>
          <button
            onClick={() => setShowPostCreateBanner(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              color: '#6B7280'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 ${showPostCreateBanner ? 'pt-[16px]' : 'pt-[90px]'} pb-[140px]`}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px' }}>

          {/* Hero Section - Group Image and Title */}
          <div style={{
            display: 'flex',
            gap: '40px',
            marginBottom: '40px',
            alignItems: 'flex-start'
          }}>
            {/* Left - Group Image with Name Overlay */}
            <div style={{
              width: '300px',
              flexShrink: 0,
              position: 'relative'
            }}>
              <div style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: '#e5e7eb',
                position: 'relative'
              }}>
                {group?.cover_image_url ? (
                  <img
                    src={group.cover_image_url}
                    alt={group.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#BB4D9A'
                  }}>
                    <ImageIcon size={64} color="white" />
                  </div>
                )}
                {/* Group Name Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  right: '16px',
                  backgroundColor: 'rgba(187, 77, 154, 0.9)',
                  padding: '12px 16px',
                  borderRadius: '8px'
                }}>
                  <p style={{
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '700',
                    margin: 0,
                    textAlign: 'center'
                  }}>
                    {group?.name || t('landingPage.groupName')}
                  </p>
                </div>
              </div>
            </div>

            {/* Right - Group Title */}
            <div style={{ flex: 1, paddingTop: '20px' }}>
              <h1 style={{
                fontSize: '48px',
                fontWeight: '700',
                color: '#BB4D9A',
                lineHeight: '1.1',
                marginBottom: '16px'
              }}>
                {group?.name || t('landingPage.groupName')}
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {t('landingPage.instructions')}
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '16px'
              }}>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: isPublished ? '#059669' : '#6b7280',
                  backgroundColor: isPublished ? '#d1fae5' : '#f3f4f6',
                  padding: '4px 12px',
                  borderRadius: '12px'
                }}>
                  {isPublished ? t('landingPage.published') : t('landingPage.draft')}
                </span>
              </div>
            </div>
          </div>

          {/* Two Column Content Area */}
          <div style={{
            display: 'flex',
            gap: '40px'
          }}>
            {/* Left Column - 65% */}
            <div style={{ flex: '0 0 65%' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    {t('landingPage.mainContent')}
                  </label>
                  <button
                    onClick={() => setShowLeftCropper(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: '#BB4D9A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <Plus size={14} />
                    {t('landingPage.addImage')}
                  </button>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <FormattingToolbar onFormat={(type) => formatColumn('left', type)} className="inline-flex items-center gap-1 border border-gray-300 rounded-lg p-1" />
                </div>
                <textarea
                  ref={leftTextareaRef}
                  value={leftColumnContent}
                  onChange={(e) => {
                    setLeftColumnContent(e.target.value);
                    setLeftCursorPos(e.target.selectionStart);
                  }}
                  onSelect={(e) => setLeftCursorPos(e.target.selectionStart)}
                  onClick={(e) => setLeftCursorPos(e.target.selectionStart)}
                  onKeyUp={(e) => setLeftCursorPos(e.target.selectionStart)}
                  placeholder={t('landingPage.textareaPlaceholder')}
                  style={{
                    width: '100%',
                    minHeight: '300px',
                    padding: '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />

                {/* Left Column Image List */}
                {leftInlineImages.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                      {t('landingPage.inlineImages', { count: leftInlineImages.length })}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                      {leftInlineImages.map(img => (
                        <div key={img.id} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <div style={{
                            position: 'relative',
                            width: '80px',
                            height: '80px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb'
                          }}>
                            <img
                              src={img.thumbnailUrl || img.displayUrl}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              onClick={() => handleRemoveLeftImage(img.id)}
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'rgba(220, 38, 38, 0.9)',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <X size={12} color="white" />
                            </button>
                          </div>
                          {/* Size toggle buttons */}
                          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                            {['S', 'M', 'L'].map(size => (
                              <button
                                key={size}
                                onClick={() => handleLeftImageSize(img.id, size)}
                                style={{
                                  width: '24px',
                                  height: '20px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  backgroundColor: (img.size || 'L') === size ? '#BB4D9A' : '#e5e7eb',
                                  color: (img.size || 'L') === size ? 'white' : '#6b7280'
                                }}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview */}
                {leftColumnContent && (
                  <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '12px' }}>
                      {t('landingPage.preview')}
                    </p>
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      {renderContentWithImages(leftColumnContent, leftInlineImages)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - 35% */}
            <div style={{ flex: '0 0 35%' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    {t('landingPage.secondaryContent')}
                  </label>
                  <button
                    onClick={() => setShowRightCropper(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: '#BB4D9A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <Plus size={14} />
                    {t('landingPage.addImage')}
                  </button>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <FormattingToolbar onFormat={(type) => formatColumn('right', type)} className="inline-flex items-center gap-1 border border-gray-300 rounded-lg p-1" />
                </div>
                <textarea
                  ref={rightTextareaRef}
                  value={rightColumnContent}
                  onChange={(e) => {
                    setRightColumnContent(e.target.value);
                    setRightCursorPos(e.target.selectionStart);
                  }}
                  onSelect={(e) => setRightCursorPos(e.target.selectionStart)}
                  onClick={(e) => setRightCursorPos(e.target.selectionStart)}
                  onKeyUp={(e) => setRightCursorPos(e.target.selectionStart)}
                  placeholder={t('landingPage.textareaPlaceholder')}
                  style={{
                    width: '100%',
                    minHeight: '300px',
                    padding: '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />

                {/* Right Column Image List */}
                {rightInlineImages.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                      {t('landingPage.inlineImages', { count: rightInlineImages.length })}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                      {rightInlineImages.map(img => (
                        <div key={img.id} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <div style={{
                            position: 'relative',
                            width: '80px',
                            height: '80px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb'
                          }}>
                            <img
                              src={img.thumbnailUrl || img.displayUrl}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              onClick={() => handleRemoveRightImage(img.id)}
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'rgba(220, 38, 38, 0.9)',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <X size={12} color="white" />
                            </button>
                          </div>
                          {/* Size toggle buttons */}
                          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                            {['S', 'M', 'L'].map(size => (
                              <button
                                key={size}
                                onClick={() => handleRightImageSize(img.id, size)}
                                style={{
                                  width: '24px',
                                  height: '20px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  backgroundColor: (img.size || 'L') === size ? '#BB4D9A' : '#e5e7eb',
                                  color: (img.size || 'L') === size ? 'white' : '#6b7280'
                                }}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview */}
                {rightColumnContent && (
                  <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '12px' }}>
                      {t('landingPage.preview')}
                    </p>
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      {renderContentWithImages(rightColumnContent, rightInlineImages)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer with Preview Button */}
      <FooterContainer viewType="form">
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
          }}>
            {/* Preview Button */}
            <button
              onClick={() => setShowPreview(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                height: '50px',
                padding: '0 24px',
                backgroundColor: '#6366F1',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Eye size={18} />
              {t('landingPage.previewButton')}
            </button>

            {/* Save as Draft */}
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              style={{
                height: '50px',
                padding: '0 24px',
                backgroundColor: '#BB4D9A',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: saving ? 'wait' : 'pointer',
                opacity: saving ? 0.7 : 1,
                transition: 'opacity 0.2s ease'
              }}
              onMouseOver={(e) => { if (!saving) e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={(e) => { if (!saving) e.currentTarget.style.opacity = '1'; }}
            >
              {saving ? t('landingPage.saving') : t('landingPage.saveDraft')}
            </button>

            {/* Cancel */}
            <button
              onClick={handleCancel}
              style={{
                height: '50px',
                padding: '0 24px',
                backgroundColor: '#FF6B6B',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              {t('landingPage.cancel')}
            </button>

            {/* Publish */}
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              style={{
                height: '50px',
                padding: '0 24px',
                backgroundColor: '#5DBEA3',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: saving ? 'wait' : 'pointer',
                opacity: saving ? 0.7 : 1,
                transition: 'opacity 0.2s ease'
              }}
              onMouseOver={(e) => { if (!saving) e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={(e) => { if (!saving) e.currentTarget.style.opacity = '1'; }}
            >
              {t('landingPage.publish')}
            </button>
          </div>
        </div>
      </FooterContainer>

      {/* Confirmation Modal */}
      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('landingPage.title')}
        message={modal.message}
        okLabel={modal.okLabel}
        cancelLabel={modal.cancelLabel}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); }
        }}
        onCancel={() => {
          try {
            if (modal.onCancel) modal.onCancel();
            else setModal({ visible: false, message: '', onOk: null });
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
          }
        }}
      />

      {/* Left Column Image Cropper */}
      <ImageCropper
        isOpen={showLeftCropper}
        onClose={() => setShowLeftCropper(false)}
        onSave={handleLeftImageSave}
        aspectMode="inline"
        bucket="inline-images"
        userId={userId}
      />

      {/* Right Column Image Cropper */}
      <ImageCropper
        isOpen={showRightCropper}
        onClose={() => setShowRightCropper(false)}
        onSave={handleRightImageSave}
        aspectMode="inline"
        bucket="inline-images"
        userId={userId}
      />

      {/* Landing Page Preview Modal */}
      <LandingPagePreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        group={group}
        leftColumnContent={leftColumnContent}
        rightColumnContent={rightColumnContent}
        leftInlineImages={leftInlineImages}
        rightInlineImages={rightInlineImages}
      />
    </div>
  );
};

export default AddEditLandingPageGroup;
